import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../../../../lib/utils";
import { microLabel, accentBarSection } from "../../../../lib/design-system";
import {
  createCourseInProgram,
  listTeachersForPicker,
  listSemestersForPicker,
} from "../../../../services/program.service";

const inputBase =
  "w-full px-3 py-2 rounded-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-accent1 transition-colors";

const Field = ({ label, htmlFor, hint, children }) => (
  <div>
    <label
      htmlFor={htmlFor}
      className="block text-xs uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400 mb-1.5"
    >
      {label}
    </label>
    {children}
    {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
  </div>
);

const CourseForm = ({ programId, onClose }) => {
  const [form, setForm] = useState({
    courseCode: "",
    title: "",
    aboutCourse: "",
    semester: "",
    teacherId: "",
  });
  const [teachers, setTeachers] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loadingPickers, setLoadingPickers] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose(false);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    const loadPickers = async () => {
      try {
        const [tRes, sRes] = await Promise.all([
          listTeachersForPicker(),
          listSemestersForPicker(),
        ]);
        if (cancelled) return;
        setTeachers(tRes.teachers || []);
        setSemesters(sRes.semesters || []);
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.data?.error ||
              "Failed to load teachers / semesters. Both must exist in the system before you can create a course."
          );
        }
      } finally {
        if (!cancelled) setLoadingPickers(false);
      }
    };
    loadPickers();
    return () => {
      cancelled = true;
    };
  }, []);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const payload = {
      courseCode: form.courseCode.trim().toUpperCase(),
      title: form.title.trim(),
      aboutCourse: form.aboutCourse.trim(),
      semester: form.semester,
      teacherId: form.teacherId,
    };

    if (
      !payload.courseCode ||
      !payload.title ||
      !payload.aboutCourse ||
      !payload.semester ||
      !payload.teacherId
    ) {
      setError("All fields are required.");
      return;
    }
    if (!/^[A-Z0-9_\-]{2,20}$/.test(payload.courseCode)) {
      setError("Course code: 2-20 chars, A-Z 0-9 _ - only.");
      return;
    }

    setSubmitting(true);
    try {
      await createCourseInProgram(programId, payload);
      toast.success(`Course ${payload.courseCode} created`);
      onClose(true);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
        onClick={() => onClose(false)}
      >
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.98 }}
          transition={{ duration: 0.25 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white dark:bg-gray-800 rounded-sm border border-gray-200 dark:border-gray-700 shadow-md w-full max-w-2xl overflow-hidden max-h-[90vh] flex flex-col"
        >
          <div className="flex items-start justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div>
              <div className={microLabel}>New Course</div>
              <h2 className="mt-1 text-2xl font-black tracking-tight text-gray-900 dark:text-white">
                Create a new course in this program
              </h2>
              <div className={cn(accentBarSection, "mt-3")} />
            </div>
            <button
              onClick={() => onClose(false)}
              className="p-2 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form
            onSubmit={handleSubmit}
            className="p-6 space-y-5 overflow-y-auto"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field
                label="Course Code *"
                htmlFor="courseCode"
                hint="2–20 chars: A–Z, 0–9, _ or -. Example: CS101"
              >
                <input
                  id="courseCode"
                  type="text"
                  value={form.courseCode}
                  onChange={(e) =>
                    setField("courseCode", e.target.value.toUpperCase())
                  }
                  placeholder="CS101"
                  required
                  className={inputBase}
                />
              </Field>
              <Field label="Title *" htmlFor="title">
                <input
                  id="title"
                  type="text"
                  value={form.title}
                  onChange={(e) => setField("title", e.target.value)}
                  placeholder="Data Structures and Algorithms"
                  required
                  className={inputBase}
                />
              </Field>
              <Field
                label="Teacher *"
                htmlFor="teacherId"
                hint={
                  loadingPickers
                    ? "Loading…"
                    : `${teachers.length} teachers available`
                }
              >
                <select
                  id="teacherId"
                  value={form.teacherId}
                  onChange={(e) => setField("teacherId", e.target.value)}
                  required
                  disabled={loadingPickers}
                  className={inputBase}
                >
                  <option value="">Select…</option>
                  {teachers.map((t) => (
                    <option key={t._id} value={t._id}>
                      {t.name}
                      {t.department ? ` · ${t.department}` : ""}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label="Semester *"
                htmlFor="semester"
                hint={
                  loadingPickers
                    ? "Loading…"
                    : `${semesters.length} semesters available`
                }
              >
                <select
                  id="semester"
                  value={form.semester}
                  onChange={(e) => setField("semester", e.target.value)}
                  required
                  disabled={loadingPickers}
                  className={inputBase}
                >
                  <option value="">Select…</option>
                  {semesters.map((s) => (
                    <option key={s._id} value={s._id}>
                      {s.name}
                      {s.semNumber ? ` (Sem ${s.semNumber})` : ""}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <Field label="About *" htmlFor="aboutCourse">
              <textarea
                id="aboutCourse"
                value={form.aboutCourse}
                onChange={(e) => setField("aboutCourse", e.target.value)}
                rows={4}
                placeholder="Brief description of what this course covers."
                required
                className={cn(inputBase, "resize-y")}
              />
            </Field>

            {teachers.length === 0 && !loadingPickers && !error && (
              <div className="p-3 rounded-sm border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 text-sm">
                No teachers exist yet. Create at least one teacher user
                (User + Teacher document) before creating a course.
              </div>
            )}
            {semesters.length === 0 && !loadingPickers && !error && (
              <div className="p-3 rounded-sm border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 text-sm">
                No semesters exist yet. Create a Semester first via the
                existing semester management flow.
              </div>
            )}

            {error && (
              <div className="p-3 rounded-sm border border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">
                {error}
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => onClose(false)}
                className="px-4 py-2 rounded-sm border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 font-semibold transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={
                  submitting ||
                  loadingPickers ||
                  teachers.length === 0 ||
                  semesters.length === 0
                }
                className={cn(
                  "px-5 py-2 rounded-sm bg-accent1 text-white font-semibold transition-opacity",
                  submitting ||
                    loadingPickers ||
                    teachers.length === 0 ||
                    semesters.length === 0
                    ? "opacity-60 cursor-not-allowed"
                    : "hover:opacity-90"
                )}
              >
                {submitting ? "Creating…" : "Create course"}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CourseForm;
